import { useEffect, useRef } from "react";

/**
 * `setInterval`, but ticked from a Web Worker so it keeps firing in a hidden tab.
 *
 * See `../lib/tickerWorker.ts` for why: Chrome's intensive throttling clamps
 * main-thread timers to ~1/minute after 5 minutes hidden, which is longer than
 * a direct call's 45-second ring window.
 *
 * Falls back to a plain `setInterval` if a Worker cannot be constructed (CSP,
 * jsdom under vitest, or a worker script that fails to load). The fallback is
 * throttled in a background tab — it restores the old behaviour rather than
 * matching the new one — so it is reported once via `onFallback` instead of
 * failing silently.
 *
 * `callback` is held in a ref, so an inline arrow does not tear down and
 * rebuild the worker on every render.
 */
export function useWorkerInterval(
  callback: () => void,
  periodMs: number,
  enabled: boolean,
  onFallback?: (reason: string) => void,
): void {
  const callbackRef = useRef(callback);
  const fallbackRef = useRef(onFallback);

  useEffect(() => {
    callbackRef.current = callback;
    fallbackRef.current = onFallback;
  }, [callback, onFallback]);

  useEffect(() => {
    if (!enabled) return;

    let worker: Worker | null = null;
    let fallbackTimer: number | null = null;
    let disposed = false;

    const fire = () => {
      callbackRef.current();
    };

    const startFallback = (reason: string) => {
      // Guard against both the constructor throwing *and* a later onerror
      // arriving: only one ticker should ever be running.
      if (disposed || fallbackTimer !== null) return;
      fallbackRef.current?.(reason);
      fallbackTimer = window.setInterval(fire, periodMs);
    };

    try {
      // The `new URL(..., import.meta.url)` form is what lets Vite discover the
      // worker at build time and emit it as its own chunk. Keep it literal.
      worker = new Worker(new URL("../lib/tickerWorker.ts", import.meta.url), {
        type: "module",
      });
      worker.onmessage = fire;
      worker.onerror = () => {
        // Construction succeeded but the script failed to load/parse. Drop the
        // dead worker and degrade to a main-thread timer.
        worker?.terminate();
        worker = null;
        startFallback("worker script failed to load");
      };
      worker.postMessage({ type: "start", periodMs });
    } catch (err) {
      worker = null;
      startFallback(err instanceof Error ? err.message : "Worker unavailable");
    }

    return () => {
      disposed = true;
      if (worker) {
        worker.postMessage({ type: "stop" });
        worker.terminate();
        worker = null;
      }
      if (fallbackTimer !== null) {
        window.clearInterval(fallbackTimer);
        fallbackTimer = null;
      }
    };
  }, [enabled, periodMs]);
}
